import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PageTransition, FadeIn, SlideIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, ChevronRight, Upload, Check, Loader2, ShoppingBag, Camera, Lightbulb, Hand } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import pressOnbloc  from '../assets/images/pressonbloc.png';
import { toAbsoluteUrl } from '../lib/utils';


const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PressOnNailsPage() {
  const [config, setConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [formData, setFormData] = useState({
    shape: '',
    length: '',
    color: '',
    customColor: '#E8C4B8',
    nailArt: 'none',
    nailArtDescription: '',
    leftHandPhoto: null,
    rightHandPhoto: null,
    inspirationPhoto: null,
    photos: [] // Keep for backwards compatibility
  });
  const [errors, setErrors] = useState({});
  const [uploadingPhotos, setUploadingPhotos] = useState({
    leftHandPhoto: false,
    rightHandPhoto: false,
    inspirationPhoto: false
  });
  const [currentStep, setCurrentStep] = useState(0);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const steps = [
    { key: 'architecture', label: 'Architecture', subtitle: 'Forme & longueur' },
    { key: 'base', label: 'La base', subtitle: 'Couleur' },
    { key: 'nail_art', label: 'Nail Art', subtitle: 'Styles & inspiration' },
    { key: 'fitting', label: 'Fitting', subtitle: 'Photos des mains' },
    { key: 'summary', label: 'Récapitulatif', subtitle: 'Validation' }
  ];

  // Fetch config from API
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/press-on-nails/config`);
        setConfig(response.data);
        // Set default values
        if (response.data.colors?.length > 0) {
          setFormData(prev => ({ ...prev, color: response.data.colors[0].value }));
        }
      } catch (err) {
        console.error('Error fetching config:', err);
        toast.error('Erreur lors du chargement de la configuration');
      } finally {
        setIsLoadingConfig(false);
      }
    };
    fetchConfig();
  }, []);

  const calculatePrice = () => {
    if (!config) return 0;
    
    let price = config.base_price;
    
    // Shape modifier
    const shapeOption = config.shapes.find(s => s.value === formData.shape);
    if (shapeOption) price += shapeOption.price_modifier;
    
    // Length modifier
    const lengthOption = config.lengths.find(l => l.value === formData.length);
    if (lengthOption) price += lengthOption.price_modifier;
    
    // Color modifier
    const colorOption = config.colors.find(c => c.value === formData.color);
    if (colorOption) price += colorOption.price_modifier;
    
    // Nail art modifier
    const nailArtOption = config.nail_art_options.find(n => n.value === formData.nailArt);
    if (nailArtOption) price += nailArtOption.price_modifier;
    
    return price;
  };

  const photoLabels = {
    leftHandPhoto: "la main gauche",
    rightHandPhoto: "la main droite",
    inspirationPhoto: "l'inspiration"
  };

  const validatePhotoField = (photoType, required) => {
    const photo = formData[photoType];
    if (!required && !photo) return "";
    if (!photo) {
      if (photoType === "inspirationPhoto") {
        return "La photo d'inspiration est requise pour le nail art";
      }
      return `La photo de ${photoLabels[photoType]} est requise`;
    }
    if (uploadingPhotos[photoType]) {
      return `Upload de la photo de ${photoLabels[photoType]} en cours`;
    }
    if (!photo.url || photo.url.startsWith("blob:")) {
      return `L'upload de la photo de ${photoLabels[photoType]} a échoué`;
    }
    return "";
  };

  // Handle specific photo upload (left hand, right hand, inspiration)
  const handleSpecificPhotoUpload = (photoType) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const previousPreview = formData[photoType]?.preview;
    if (previousPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(previousPreview);
    }

    const preview = URL.createObjectURL(file);
    setUploadingPhotos(prev => ({ ...prev, [photoType]: true }));
    setErrors(prev => {
      const next = { ...prev };
      delete next[photoType];
      return next;
    });
    setFormData(prev => ({
      ...prev,
      [photoType]: {
        preview,
        url: null,
        public_id: null,
        uploaded_at: null
      }
    }));

    try {
      const payload = new FormData();
      payload.append("file", file);
      const response = await axios.post(
        `${API_URL}/uploads/order-reference-image`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const uploadedUrl = toAbsoluteUrl(response.data?.image_url);
      if (!uploadedUrl) {
        throw new Error("Missing uploaded image URL");
      }

      setFormData(prev => ({
        ...prev,
        [photoType]: {
          preview,
          url: uploadedUrl,
          public_id: null,
          uploaded_at: new Date().toISOString()
        }
      }));
    } catch (err) {
      console.error("Error uploading reference image:", err);
      setErrors(prev => ({
        ...prev,
        [photoType]: `L'upload de la photo de ${photoLabels[photoType]} a échoué`
      }));
      toast.error("Upload image échoué. Réessaie avec une autre image.");
    } finally {
      setUploadingPhotos(prev => ({ ...prev, [photoType]: false }));
    }
  };

  // Remove a specific photo
  const removeSpecificPhoto = (photoType) => {
    const preview = formData[photoType]?.preview;
    if (preview?.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
    setUploadingPhotos(prev => ({ ...prev, [photoType]: false }));
    setErrors(prev => {
      const next = { ...prev };
      delete next[photoType];
      return next;
    });
    setFormData(prev => ({
      ...prev,
      [photoType]: null
    }));
  };

  // Check if inspiration photo is required (only when nail art is selected)
  const isInspirationPhotoRequired = () => {
    return formData.nailArt !== 'none';
  };

  const validateForm = () => {
    if (!config) return false;
    
    const newErrors = {};
    
    if (!formData.shape) newErrors.shape = 'La forme est requise';
    if (!formData.length) newErrors.length = 'La longueur est requise';
    if (!formData.color) newErrors.color = 'La couleur est requise';
    
    // Check nail art description - REQUIRED if nail art is "custom" (Personnalisé)
    if (formData.nailArt === 'custom') {
      if (!formData.nailArtDescription.trim()) {
        newErrors.nailArtDescription = 'Pour un nail art personnalisé, veuillez décrire votre demande';
      }
    } else if (formData.nailArt !== 'none' && config.nail_art_description_required && !formData.nailArtDescription.trim()) {
      newErrors.nailArtDescription = 'Décrivez le nail art souhaité';
    }
    
    // Photo validation - left and right hand always required
    const leftPhotoError = validatePhotoField('leftHandPhoto', true);
    if (leftPhotoError) newErrors.leftHandPhoto = leftPhotoError;
    const rightPhotoError = validatePhotoField('rightHandPhoto', true);
    if (rightPhotoError) newErrors.rightHandPhoto = rightPhotoError;
    
    // Inspiration photo required ONLY if nail art is selected
    const inspirationPhotoError = validatePhotoField('inspirationPhoto', isInspirationPhotoRequired());
    if (inspirationPhotoError) newErrors.inspirationPhoto = inspirationPhotoError;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep = (stepIndex) => {
    if (!config) return false;
    let isValid = true;
    const nextErrors = { ...errors };
    const setFieldError = (field, message) => {
      if (message) {
        nextErrors[field] = message;
        isValid = false;
      } else {
        delete nextErrors[field];
      }
    };

    if (stepIndex === 0) {
      setFieldError('shape', formData.shape ? '' : 'La forme est requise');
      setFieldError('length', formData.length ? '' : 'La longueur est requise');
    }

    if (stepIndex === 1) {
      setFieldError('color', formData.color ? '' : 'La couleur est requise');
    }

    if (stepIndex === 2) {
      if (formData.nailArt === 'custom') {
        setFieldError(
          'nailArtDescription',
          formData.nailArtDescription.trim()
            ? ''
            : 'Pour un nail art personnalisé, veuillez décrire votre demande'
        );
      } else if (
        formData.nailArt !== 'none' &&
        config.nail_art_description_required &&
        !formData.nailArtDescription.trim()
      ) {
        setFieldError('nailArtDescription', 'Décrivez le nail art souhaité');
      } else {
        setFieldError('nailArtDescription', '');
      }

      setFieldError(
        'inspirationPhoto',
        validatePhotoField('inspirationPhoto', isInspirationPhotoRequired())
      );
    }

    if (stepIndex === 3) {
      setFieldError('leftHandPhoto', validatePhotoField('leftHandPhoto', true));
      setFieldError('rightHandPhoto', validatePhotoField('rightHandPhoto', true));
    }

    setErrors(nextErrors);
    return isValid;
  };

  const handleNextStep = () => {
    if (!validateStep(currentStep)) {
      toast.error('Veuillez compléter cette étape avant de continuer');
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleStepSelect = (index) => {
    if (index <= currentStep) {
      setCurrentStep(index);
    }
  };

  const handleAddToCart = () => {
    if (currentStep < steps.length - 1) {
      handleNextStep();
      return;
    }
    if (!validateForm()) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }
    
    // Get labels for display
    const shapeOption = config.shapes.find(s => s.value === formData.shape);
    const lengthOption = config.lengths.find(l => l.value === formData.length);
    const colorOption = config.colors.find(c => c.value === formData.color);
    const nailArtOption = config.nail_art_options.find(n => n.value === formData.nailArt);
    
    // Build photos array from individual photo fields
    const photos = [];
    if (formData.leftHandPhoto) {
      photos.push({
        type: 'left_hand',
        preview: formData.leftHandPhoto.preview,
        url: formData.leftHandPhoto.url,
        public_id: formData.leftHandPhoto.public_id || null,
        uploaded_at: formData.leftHandPhoto.uploaded_at || null
      });
    }
    if (formData.rightHandPhoto) {
      photos.push({
        type: 'right_hand',
        preview: formData.rightHandPhoto.preview,
        url: formData.rightHandPhoto.url,
        public_id: formData.rightHandPhoto.public_id || null,
        uploaded_at: formData.rightHandPhoto.uploaded_at || null
      });
    }
    if (formData.inspirationPhoto) {
      photos.push({
        type: 'inspiration',
        preview: formData.inspirationPhoto.preview,
        url: formData.inspirationPhoto.url,
        public_id: formData.inspirationPhoto.public_id || null,
        uploaded_at: formData.inspirationPhoto.uploaded_at || null
      });
    }
    
    const cartItem = {
      type: 'press_on_nails',
      shape: formData.shape,
      shapeLabel: shapeOption?.label,
      length: formData.length,
      lengthLabel: lengthOption?.label,
      color: formData.color === 'custom' ? formData.customColor : formData.color,
      colorLabel: colorOption?.label,
      nailArt: formData.nailArt,
      nailArtLabel: nailArtOption?.label,
      nailArtDescription: formData.nailArtDescription,
      photos: photos,
      totalPrice: calculatePrice()
    };
    
    addItem(cartItem);
    toast.success('Article ajouté au panier !');
    
    // Reset form
    setFormData({
      shape: '',
      length: '',
      color: config.colors?.[0]?.value || '',
      customColor: '#E8C4B8',
      nailArt: 'none',
      nailArtDescription: '',
      leftHandPhoto: null,
      rightHandPhoto: null,
      inspirationPhoto: null,
      photos: []
    });
    setErrors({});
    setUploadingPhotos({
      leftHandPhoto: false,
      rightHandPhoto: false,
      inspirationPhoto: false
    });
    setCurrentStep(0);
  };

  const handleGoToCart = () => {
    navigate('/panier');
  };

  const progressPercent = steps.length > 1
    ? Math.round((currentStep / (steps.length - 1)) * 100)
    : 0;
  const selectedShape = config?.shapes.find(s => s.value === formData.shape);
  const selectedLength = config?.lengths.find(l => l.value === formData.length);
  const selectedColor = config?.colors.find(c => c.value === formData.color);
  const selectedNailArt = config?.nail_art_options.find(n => n.value === formData.nailArt);

  if (isLoadingConfig) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden" data-testid="pressonnails-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${pressOnbloc})`,
            filter: 'brightness(0.5)'
          }}
        />
        <div className="relative z-10 text-center text-white px-6">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--rose-gold)] mb-4">
            Boutique
          </p>
          <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-7xl font-light mb-4">
            Press On Nails
          </h1>
          <p className="text-lg text-white/80 max-w-xl mx-auto">
            Faux ongles personnalisés sur mesure
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)] transition-colors">Accueil</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Press On Nails</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-24 px-6" data-testid="pressonnails-content">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Info Section */}
            <SlideIn direction="left">
              <div>
                <FadeIn>
                  <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)] mb-4">
                    Création sur mesure
                  </p>
                  <h2 className="font-['Cormorant_Garamond'] text-4xl font-light mb-6 text-[var(--charcoal)]">
                    Vos ongles, votre style
                  </h2>
                  <p className="text-[var(--charcoal)]/70 mb-6 leading-relaxed">
                    Les Press On Nails sont des faux ongles réutilisables, créés sur mesure selon vos envies. 
                    Choisissez la forme, la longueur, la couleur et ajoutez du nail art pour un résultat unique.
                  </p>
                </FadeIn>

                <div className="space-y-4 mb-8">
                  {[
                    { title: 'Réutilisables', desc: "Réutilisable à l’infini avec une utilisation et un entretien approprié" },
                    { title: 'Pose facile', desc: 'Kit de pose inclus avec instructions détaillées' },
                    { title: 'Personnalisés', desc: 'Créés selon vos mesures et vos goûts' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[var(--gold)]/10 flex items-center justify-center mt-1">
                        <Check size={14} className="text-[var(--gold)]" />
                      </div>
                      <div>
                        <h4 className="font-medium text-[var(--charcoal)]">{item.title}</h4>
                        <p className="text-sm text-[var(--charcoal)]/60">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-6 bg-[var(--champagne)]/30 border border-[var(--champagne)]">
                  <h4 className="font-['Cormorant_Garamond'] text-xl mb-2 text-[var(--charcoal)]">
                    Délai de fabrication
                  </h4>
                  <p className="text-sm text-[var(--charcoal)]/70">
                    Comptez 5 à 7 jours ouvrés pour la création de votre set personnalisé.
                    Livraison en point relais ou à domicile.
                  </p>
                </div>
              </div>
            </SlideIn>

            {/* Form Section */}
            <SlideIn direction="right">
              <form onSubmit={(e) => e.preventDefault()} className="bg-white p-8 border border-[var(--champagne)]" data-testid="pressonnails-form">
                <div className="mb-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)]">
                        Configurateur
                      </p>
                      <h3 className="font-['Cormorant_Garamond'] text-2xl text-[var(--charcoal)]">
                        Créez votre set
                      </h3>
                    </div>
                    <span className="text-xs text-[var(--charcoal)]/60">
                      Étape {currentStep + 1}/{steps.length}
                    </span>
                  </div>

                  <div className="mt-6 grid grid-cols-5 gap-2">
                    {steps.map((step, index) => {
                      const isActive = index === currentStep;
                      const isDone = index < currentStep;
                      return (
                        <button
                          key={step.key}
                          type="button"
                          onClick={() => handleStepSelect(index)}
                          className="flex flex-col items-center gap-2 text-center"
                          disabled={index > currentStep}
                        >
                          <span
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs border transition-colors ${
                              isActive
                                ? 'bg-[var(--gold)] text-white border-[var(--gold)]'
                                : isDone
                                  ? 'bg-[var(--champagne)] text-[var(--charcoal)] border-[var(--champagne)]'
                                  : 'bg-white text-[var(--charcoal)]/40 border-[var(--champagne)]'
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span className={`hidden sm:block text-[10px] uppercase tracking-[0.2em] ${isActive ? 'text-[var(--charcoal)]' : 'text-[var(--charcoal)]/50'}`}>
                            {step.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4 h-[2px] bg-[var(--champagne)] relative overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-[var(--gold)] transition-all duration-300"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)]">
                    {steps[currentStep].label}
                  </p>
                  <p className="text-sm text-[var(--charcoal)]/60 mt-1">
                    {steps[currentStep].subtitle}
                  </p>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.25 }}
                  >
                    {currentStep === 0 && (
                      <div className="space-y-6">
                        <div>
                          <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-2 block">
                            Forme <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={formData.shape}
                            onValueChange={(value) => setFormData(prev => ({ ...prev, shape: value }))}
                          >
                            <SelectTrigger
                              className={`w-full ${errors.shape ? 'border-red-500' : ''}`}
                              data-testid="select-shape"
                            >
                              <SelectValue placeholder="Choisir une forme" />
                            </SelectTrigger>
                            <SelectContent>
                              {config?.shapes?.map(shape => (
                                <SelectItem key={shape.value} value={shape.value}>
                                  {shape.label} {shape.price_modifier > 0 && `(+${shape.price_modifier}€)`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.shape && <p className="text-red-500 text-xs mt-1">{errors.shape}</p>}
                        </div>

                        <div>
                          <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-2 block">
                            Longueur <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={formData.length}
                            onValueChange={(value) => setFormData(prev => ({ ...prev, length: value }))}
                          >
                            <SelectTrigger
                              className={`w-full ${errors.length ? 'border-red-500' : ''}`}
                              data-testid="select-length"
                            >
                              <SelectValue placeholder="Choisir une longueur" />
                            </SelectTrigger>
                            <SelectContent>
                              {config?.lengths?.map(length => (
                                <SelectItem key={length.value} value={length.value}>
                                  {length.label} {length.price_modifier > 0 && `(+${length.price_modifier}€)`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.length && <p className="text-red-500 text-xs mt-1">{errors.length}</p>}
                        </div>
                      </div>
                    )}

                    {currentStep === 1 && (
                      <div className="space-y-6">
                        <div>
                          <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-3 block">
                            Couleur <span className="text-red-500">*</span>
                          </Label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            {config?.colors?.filter(c => c.value !== 'custom').map(color => {
                              const imageUrl = color.image_url ? toAbsoluteUrl(color.image_url) : null;
                              const isSelected = formData.color === color.value;
                              return (
                                <button
                                  key={color.value}
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                                  className="text-left"
                                  data-testid={`color-${color.value}`}
                                >
                                  <div className={`relative aspect-square overflow-hidden border transition-all ${isSelected ? 'border-[var(--gold)] ring-2 ring-[var(--gold)]' : 'border-[var(--champagne)]'}`}>
                                    {imageUrl ? (
                                      <img src={imageUrl} alt={color.label} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="absolute inset-0" style={{ backgroundColor: color.value }} />
                                    )}
                                    {isSelected && (
                                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white flex items-center justify-center text-[var(--gold)] shadow">
                                        <Check size={14} />
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[var(--charcoal)]">
                                    {color.label}
                                  </div>
                                  {color.price_modifier > 0 && (
                                    <div className="text-[11px] text-[var(--gold)]">+{color.price_modifier}€</div>
                                  )}
                                </button>
                              );
                            })}
                          </div>

                          <div className="mt-6 p-4 border border-[var(--champagne)] bg-[var(--champagne)]/10">
                            <div className="flex items-center justify-between gap-4">
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, color: 'custom' }))}
                                className={`text-xs uppercase tracking-[0.2em] ${
                                  formData.color === 'custom' ? 'text-[var(--gold)]' : 'text-[var(--charcoal)]/70'
                                }`}
                              >
                                Couleur personnalisée
                              </button>
                              {formData.color === 'custom' && (
                                <input
                                  type="color"
                                  value={formData.customColor}
                                  onChange={(e) => setFormData(prev => ({ ...prev, customColor: e.target.value }))}
                                  className="w-12 h-8 border border-[var(--champagne)] cursor-pointer"
                                />
                              )}
                            </div>
                          </div>
                          {errors.color && <p className="text-red-500 text-xs mt-2">{errors.color}</p>}
                        </div>
                      </div>
                    )}

                    {currentStep === 2 && (
                      <div className="space-y-6">
                        <div>
                          <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-3 block">
                            Style de Nail Art
                          </Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {config?.nail_art_options?.map(option => {
                              const imageUrl = option.image_url ? toAbsoluteUrl(option.image_url) : null;
                              const isSelected = formData.nailArt === option.value;
                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => {
                                    if (option.value === 'none') {
                                      const inspirationPreview = formData.inspirationPhoto?.preview;
                                      if (inspirationPreview?.startsWith('blob:')) {
                                        URL.revokeObjectURL(inspirationPreview);
                                      }
                                      setUploadingPhotos(prev => ({ ...prev, inspirationPhoto: false }));
                                      setErrors(prev => {
                                        const next = { ...prev };
                                        delete next.inspirationPhoto;
                                        return next;
                                      });
                                    }
                                    setFormData(prev => ({
                                      ...prev,
                                      nailArt: option.value,
                                      nailArtDescription: option.value === 'none' ? '' : prev.nailArtDescription,
                                      inspirationPhoto: option.value === 'none' ? null : prev.inspirationPhoto
                                    }));
                                  }}
                                  className={`border transition-all text-left ${isSelected ? 'border-[var(--gold)] shadow-[0_10px_30px_rgba(0,0,0,0.06)]' : 'border-[var(--champagne)]'}`}
                                >
                                  <div className="aspect-[4/3] bg-[var(--champagne)]/20 overflow-hidden relative">
                                    {imageUrl ? (
                                      <img src={imageUrl} alt={option.label} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="absolute inset-0 bg-gradient-to-br from-[var(--champagne)]/40 to-white" />
                                    )}
                                    {isSelected && (
                                      <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white flex items-center justify-center text-[var(--gold)] shadow">
                                        <Check size={14} />
                                      </div>
                                    )}
                                  </div>
                                  <div className="p-4">
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm font-medium text-[var(--charcoal)]">{option.label}</span>
                                      {option.price_modifier > 0 && (
                                        <span className="text-xs text-[var(--gold)]">+{option.price_modifier}€</span>
                                      )}
                                    </div>
                                    {option.description && (
                                      <p className="text-xs text-[var(--charcoal)]/60 mt-2">{option.description}</p>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {formData.nailArt !== 'none' && (
                          <div>
                            <Label className="text-xs text-[var(--charcoal)]/60 mb-1 block">
                              Description du nail art
                              {formData.nailArt === 'custom' && <span className="text-red-500"> (obligatoire)</span>}
                            </Label>
                            <Textarea
                              placeholder={formData.nailArt === 'custom'
                                ? "Décrivez précisément votre demande personnalisée (motifs, couleurs, inspirations...)"
                                : "Décrivez le nail art souhaité (motifs, couleurs, style...)"
                              }
                              value={formData.nailArtDescription}
                              onChange={(e) => setFormData(prev => ({ ...prev, nailArtDescription: e.target.value }))}
                              className={`min-h-[100px] ${errors.nailArtDescription ? 'border-red-500' : ''}`}
                              data-testid="textarea-nailart"
                            />
                            {errors.nailArtDescription && (
                              <p className="text-red-500 text-xs mt-1">{errors.nailArtDescription}</p>
                            )}
                          </div>
                        )}

                        {formData.nailArt !== 'none' && (
                          <div>
                            <Label className="text-xs uppercase tracking-wider text-[var(--charcoal)]/60 mb-2 block">
                              Photo d&apos;inspiration {isInspirationPhotoRequired() && <span className="text-red-500">*</span>}
                            </Label>
                            {formData.inspirationPhoto ? (
                              <div className="relative aspect-[4/3]">
                                <img
                                  src={formData.inspirationPhoto.preview}
                                  alt="Inspiration"
                                  className="w-full h-full object-cover border border-[var(--champagne)]"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeSpecificPhoto('inspirationPhoto')}
                                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 transition-colors"
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <label
                                className={`aspect-[4/3] border-2 border-dashed cursor-pointer flex flex-col items-center justify-center gap-2 transition-colors hover:border-[var(--gold)] ${errors.inspirationPhoto ? 'border-red-500' : isInspirationPhotoRequired() ? 'border-amber-400' : 'border-[var(--champagne)]'}`}
                                data-testid="upload-inspiration"
                              >
                                <Upload size={20} className="text-[var(--charcoal)]/40" />
                                <span className="text-xs text-[var(--charcoal)]/60 text-center px-2">
                                  {isInspirationPhotoRequired() ? 'Nail art' : 'Optionnel'}
                                </span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleSpecificPhotoUpload('inspirationPhoto')}
                                  className="hidden"
                                />
                              </label>
                            )}
                            {errors.inspirationPhoto && <p className="text-red-500 text-xs mt-1">{errors.inspirationPhoto}</p>}
                            {uploadingPhotos.inspirationPhoto && (
                              <p className="text-[var(--charcoal)]/60 text-xs mt-1">Upload en cours...</p>
                            )}
                            {isInspirationPhotoRequired() && !formData.inspirationPhoto && !errors.inspirationPhoto && (
                              <p className="text-amber-600 text-xs mt-1">Photo requise pour le nail art</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {currentStep === 3 && (
                      <div className="space-y-6">
                        <div className="p-5 bg-[var(--champagne)]/30 border border-[var(--champagne)]" data-testid="photo-guide-block">
                          <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-[var(--gold)]/10 flex items-center justify-center shrink-0">
                              <Camera size={20} className="text-[var(--gold)]" />
                            </div>
                            <div>
                              <h4 className="font-['Cormorant_Garamond'] text-lg font-medium text-[var(--charcoal)] uppercase tracking-wider">
                                Photos de vos mains
                              </h4>
                              <p className="text-sm text-[var(--charcoal)]/70 mt-1">
                                Pour choisir la bonne taille, merci de nous envoyer <strong>1 photo par main</strong> avec une pièce de <strong>1€ ou 2€</strong>.
                              </p>
                            </div>
                          </div>

                          <ul className="space-y-2 mb-4 ml-4">
                            {[
                              'Main à plat sur une surface claire et lisse.',
                              "Pièce posée près des doigts (index et majeur).",
                              'Ongles propres, sans vernis, sans filtre ni flash.',
                              'Photo prise bien de dessus, nette et bien cadrée.'
                            ].map((instruction, idx) => (
                              <li key={idx} className="text-sm text-[var(--charcoal)]/80 flex items-start gap-2">
                                <span className="text-[var(--gold)] mt-0.5">•</span>
                                {instruction}
                              </li>
                            ))}
                          </ul>

                          <div className="flex items-center gap-2 text-sm text-[var(--charcoal)]/70 italic bg-white/50 p-3 rounded">
                            <Lightbulb size={16} className="text-amber-500 shrink-0" />
                            Plus la photo est nette, plus la taille de vos capsules sera précise.
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-4 block">
                            Photos de vos mains <span className="text-red-500">*</span>
                          </Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-[var(--charcoal)] flex items-center gap-1">
                                <Hand size={14} className="text-[var(--gold)]" />
                                Main gauche <span className="text-red-500">*</span>
                              </p>
                              {formData.leftHandPhoto ? (
                                <div className="relative aspect-square">
                                  <img
                                    src={formData.leftHandPhoto.preview}
                                    alt="Main gauche"
                                    className="w-full h-full object-cover border border-[var(--champagne)]"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeSpecificPhoto('leftHandPhoto')}
                                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 transition-colors"
                                  >
                                    ×
                                  </button>
                                </div>
                              ) : (
                                <label
                                  className={`aspect-square border-2 border-dashed cursor-pointer flex flex-col items-center justify-center gap-2 transition-colors hover:border-[var(--gold)] ${errors.leftHandPhoto ? 'border-red-500' : 'border-[var(--champagne)]'}`}
                                  data-testid="upload-left-hand"
                                >
                                  <Upload size={20} className="text-[var(--charcoal)]/40" />
                                  <span className="text-xs text-[var(--charcoal)]/60 text-center px-2">Main gauche</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleSpecificPhotoUpload('leftHandPhoto')}
                                    className="hidden"
                                  />
                                </label>
                              )}
                              {errors.leftHandPhoto && <p className="text-red-500 text-xs">{errors.leftHandPhoto}</p>}
                              {uploadingPhotos.leftHandPhoto && (
                                <p className="text-[var(--charcoal)]/60 text-xs">Upload en cours...</p>
                              )}
                            </div>

                            <div className="space-y-2">
                              <p className="text-xs font-medium text-[var(--charcoal)] flex items-center gap-1">
                                <Hand size={14} className="text-[var(--gold)] transform scale-x-[-1]" />
                                Main droite <span className="text-red-500">*</span>
                              </p>
                              {formData.rightHandPhoto ? (
                                <div className="relative aspect-square">
                                  <img
                                    src={formData.rightHandPhoto.preview}
                                    alt="Main droite"
                                    className="w-full h-full object-cover border border-[var(--champagne)]"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeSpecificPhoto('rightHandPhoto')}
                                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 transition-colors"
                                  >
                                    ×
                                  </button>
                                </div>
                              ) : (
                                <label
                                  className={`aspect-square border-2 border-dashed cursor-pointer flex flex-col items-center justify-center gap-2 transition-colors hover:border-[var(--gold)] ${errors.rightHandPhoto ? 'border-red-500' : 'border-[var(--champagne)]'}`}
                                  data-testid="upload-right-hand"
                                >
                                  <Upload size={20} className="text-[var(--charcoal)]/40" />
                                  <span className="text-xs text-[var(--charcoal)]/60 text-center px-2">Main droite</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleSpecificPhotoUpload('rightHandPhoto')}
                                    className="hidden"
                                  />
                                </label>
                              )}
                              {errors.rightHandPhoto && <p className="text-red-500 text-xs">{errors.rightHandPhoto}</p>}
                              {uploadingPhotos.rightHandPhoto && (
                                <p className="text-[var(--charcoal)]/60 text-xs">Upload en cours...</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {currentStep === 4 && (
                      <div className="space-y-6">
                        <div className="p-5 border border-[var(--champagne)]">
                          <h4 className="font-['Cormorant_Garamond'] text-xl text-[var(--charcoal)] mb-4">
                            Votre sélection
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-[var(--charcoal)]/50 uppercase text-[10px] tracking-[0.2em]">Forme</p>
                              <p className="text-[var(--charcoal)]">{selectedShape?.label || '-'}</p>
                            </div>
                            <div>
                              <p className="text-[var(--charcoal)]/50 uppercase text-[10px] tracking-[0.2em]">Longueur</p>
                              <p className="text-[var(--charcoal)]">{selectedLength?.label || '-'}</p>
                            </div>
                            <div>
                              <p className="text-[var(--charcoal)]/50 uppercase text-[10px] tracking-[0.2em]">Couleur</p>
                              <div className="flex items-center gap-3">
                                <span className="text-[var(--charcoal)]">
                                  {selectedColor?.label || 'Personnalisée'}
                                </span>
                                {selectedColor?.image_url ? (
                                  <img
                                    src={toAbsoluteUrl(selectedColor.image_url)}
                                    alt={selectedColor.label}
                                    className="w-10 h-10 object-cover border border-[var(--champagne)]"
                                  />
                                ) : (
                                  <span
                                    className="w-6 h-6 border border-[var(--champagne)]"
                                    style={{ backgroundColor: formData.color === 'custom' ? formData.customColor : formData.color }}
                                  />
                                )}
                              </div>
                            </div>
                            <div>
                              <p className="text-[var(--charcoal)]/50 uppercase text-[10px] tracking-[0.2em]">Nail Art</p>
                              <p className="text-[var(--charcoal)]">{selectedNailArt?.label || '-'}</p>
                              {formData.nailArtDescription && (
                                <p className="text-xs text-[var(--charcoal)]/60 mt-1">{formData.nailArtDescription}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="p-5 border border-[var(--champagne)]">
                          <h4 className="font-['Cormorant_Garamond'] text-xl text-[var(--charcoal)] mb-4">
                            Photos fournies
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {[
                              { label: 'Main gauche', photo: formData.leftHandPhoto },
                              { label: 'Main droite', photo: formData.rightHandPhoto },
                              { label: 'Inspiration', photo: formData.inspirationPhoto }
                            ].map((item) => (
                              <div key={item.label} className="text-center">
                                <div className="aspect-square border border-[var(--champagne)] bg-[var(--champagne)]/10 overflow-hidden">
                                  {item.photo ? (
                                    <img src={item.photo.preview} alt={item.label} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-xs text-[var(--charcoal)]/40">
                                      —
                                    </div>
                                  )}
                                </div>
                                <p className="text-xs text-[var(--charcoal)]/60 mt-2">{item.label}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="p-4 bg-[var(--champagne)]/30">
                          <div className="flex justify-between items-center">
                            <span className="text-[var(--charcoal)]/70">Total estimé</span>
                            <span className="font-['Cormorant_Garamond'] text-3xl text-[var(--gold)]">
                              {calculatePrice()}€
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div className="border border-[var(--champagne)] bg-[var(--champagne)]/10 px-5 py-4 min-w-[220px]">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--dark-rosegold)]">
                      Prix de base
                    </p>
                    <div className="mt-2 flex items-end gap-2">
                      <span className="font-['Cormorant_Garamond'] text-3xl text-[var(--charcoal)]">
                        {config?.base_price ?? 0}€
                      </span>
                      <span className="text-xs text-[var(--charcoal)]/55 pb-1">
                        hors options
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePrevStep}
                      disabled={currentStep === 0}
                      className={`rounded-none ${currentStep === 0 ? 'invisible pointer-events-none' : ''}`}
                    >
                      <ChevronLeft size={16} className="mr-2" />
                      Précédent
                    </Button>
                    {currentStep < steps.length - 1 ? (
                      <Button
                        type="button"
                        onClick={handleNextStep}
                        className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none"
                      >
                        Suivant
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={handleAddToCart}
                        className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none"
                        data-testid="btn-add-to-cart"
                      >
                        <ShoppingBag size={16} className="mr-2" />
                        Ajouter au panier
                      </Button>
                    )}
                  </div>
                </div>

                {currentStep === steps.length - 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoToCart}
                    className="mt-3 w-full py-4 border-[var(--charcoal)]/20 text-xs uppercase tracking-[0.2em] rounded-none"
                    data-testid="btn-go-to-cart"
                  >
                    Voir le panier
                  </Button>
                )}
              </form>
            </SlideIn>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
